import { BufferControllerEvents } from '@bufferController/BufferController.events';
import { EDebuggableModuleState, TDebuggableAdditionalInfo } from '@customTypes/common';
import { TEventBus } from '@eventBus/EventBus';
import { Fragment } from '@fragment/Fragment';
import { EIndependenceType } from '@fragmentsQueue/IFragmentsQueue';
import { ResolutionsManagerEvents } from '@resolutionsManager/ResolutionsManager.events';
import { BasePlaylistRotationController } from '@streamLoader/BasePlaylistRotationController/BasePlaylistRotationController';
import { IBasePlaylistRotationControllerConfig } from '@streamLoader/BasePlaylistRotationController/BasePlaylistRotationController.config';
import { BasePlaylistRotationControllerEvents } from '@streamLoader/BasePlaylistRotationController/BasePlaylistRotationController.events';
import { EBlockingMode } from '@streamLoader/BasePlaylistRotationController/IBasePlaylistRotationController';
import { FragmentLoaderEvents } from '@streamLoader/FragmentLoader/FragmentLoader.events';
import { PlaylistDecryptHandler } from '@streamLoader/PlaylistDecrypt/PlaylistDecryptHandler';
import { NetworkLoader } from '@streamLoader/net/NetworkLoader';
import { getPartHoldback } from '@utils/common/partHoldback';
import bind from '@utils/decorators/bind';
import { IHydratedManifest } from '@utils/m3u8Hydrator/m3u8Hydrator';
import { getGlobalThis } from '@utils/polyfills/globalThis';

interface ILLNewPlaylistRotationControllerConstructorSignature {
  url: string;
  startManifest?: IHydratedManifest;
  initialMSN?: number;
  initialPN?: number;
  lastLoadedFragment: Fragment | null;
}
export class LLNewPlaylistRotationController extends BasePlaylistRotationController {
  public state: EDebuggableModuleState = EDebuggableModuleState.OK;
  public status: string = 'Idle';
  public additionalInfo: TDebuggableAdditionalInfo = {};
  public groups: string[] = ['loader'];
  private _playlistLoaderTimeout: ReturnType<typeof setTimeout> | null = null;
  private _nextMSN: number = -1;
  private _nextPN: number = -1;
  private _partHoldBack: number = -1;
  private _lastLoadedFragment: Fragment | null = null;
  private _awaitingFragmentURL: string | null = null;
  private _hasLoadedInitialAmount: boolean = false;
  private readonly _onSegmentLoadedListener: string;
  private readonly _onSegmentFailedListener: string;
  private readonly _onSegmentAbortedListener: string;
  private readonly _onFragmentAddedListener: string;

  constructor(
    eventBus: TEventBus,
    props: ILLNewPlaylistRotationControllerConstructorSignature,
    playlistDecryptHandler: PlaylistDecryptHandler,
  ) {
    const { url, startManifest, initialMSN, initialPN, lastLoadedFragment } = props;
    super('LLPlaylistRotationController', 'playlistLoader', playlistDecryptHandler, eventBus);

    this._lifecycleEventReporter('Created');
    this._url = url;
    this._lastLoadedFragment = lastLoadedFragment;

    if (startManifest) {
      this._handleParsedPlaylist(startManifest);
    } else {
      this._nextMSN = Number.isFinite(initialMSN) ? (initialMSN as number) : -1;
      this._nextPN = Number.isFinite(initialPN) ? (initialPN as number) : -1;
      this._loadNextPlaylist();
    }

    this._onSegmentLoadedListener = this._eventBus.subscribe(FragmentLoaderEvents.fragments.fragmentLoaded, this._onFragmentFinished);
    this._onSegmentFailedListener = this._eventBus.subscribe(FragmentLoaderEvents.fragments.fragmentLoadingError, this._onFragmentFinished);
    this._onSegmentAbortedListener = this._eventBus.subscribe(
      FragmentLoaderEvents.fragments.fragmentLoadingAborted,
      this._onFragmentFinished,
    );
    this._onFragmentAddedListener = this._eventBus.subscribe(BufferControllerEvents.fragmentAdded, this._onFragmentAdded);

    if (this.config.targetBufferMultiplier && this.config.targetBufferMultiplier < 1) {
      this._logger.warn(
        'Target buffer multiplier set to value lower than 1. It is unsafe to load less than target-duration, forcing no multiplier instead',
      );
    }
  }

  public destroy(): void {
    this._isActive = false;
    super.destroy();

    this._currentPlaylistLoader?.destroy();
    this._currentPlaylistLoader = null;
    this._logger.info('Destroy controller');

    this._eventBus.unsubscribe(this._onSegmentLoadedListener);
    this._eventBus.unsubscribe(this._onSegmentAbortedListener);
    this._eventBus.unsubscribe(this._onSegmentFailedListener);
    this._eventBus.unsubscribe(this._onFragmentAddedListener);

    this._lifecycleEventReporter('Destroyed');
  }

  public abortCurrentPlaylistLoading(): void {
    this._logger.info('Aborting current loading playlist');
    // this._currentPlaylistLoader?.destroy();
  }

  public setURL(url: string, soft: boolean): void {
    if (soft) {
      this._url = url;
      return;
    }
    this._logger.info(`Setting new url ${url}`);
    this._currentPlaylistLoader?.destroy();
    this._url = url;
    this._nextPN = -1;
    this._nextMSN = -1;
    this._loadNextPlaylist();
  }

  @bind
  public _handleConfigUpdated(updatedValues: Partial<IBasePlaylistRotationControllerConfig>): void {
    if (updatedValues.equalPlaylistsAmountToDecline) {
      this._samePlaylistsCount = 0;
    }

    if (updatedValues.lowLatencyMode === false) {
      // Immediately emit simple playlist type
      const lastPlaylistParsed: IHydratedManifest | null = this._parsePlaylist({
        data: this._lastPlaylistContent,
        isCorruptRequested: !!this._currentPlaylistLoader?.queryParams.has(PlaylistDecryptHandler.SCHEME_QUERY_PARAMETER_NAME),
      });
      if (lastPlaylistParsed) {
        this._handleSimplePlaylistDetected(lastPlaylistParsed);
      } else {
        this._eventBus.publish(ResolutionsManagerEvents.requestRestartFromMain, {});
      }
    }
  }

  protected _loadNextPlaylist(): void {
    if (!this._isActive) {
      this._logger.warn('Loader was destroying, but we trying to load next playlist!');
      return;
    }

    const { playlistLoadingTimeout } = this.config;

    this._logger.debug('Loading next playlist');
    this._lifecycleEventReporter(`Loading next playlist with url ${this._url}`);
    this.status = 'Loading playlist';

    if (this._currentPlaylistLoader) {
      this._currentPlaylistLoader.cancel(`Another playlist with url ${this._url} scheduled to load`);
    }

    const url: URL = this._buildUrl();

    this._currentPlaylistLoader = new NetworkLoader(
      {
        url,
        loadingTimeout: playlistLoadingTimeout,
        autoLoad: true,
        mode: 'text',
        onStateChanged: this._onPlaylistLoadingStateChanged,
        workerMode: this.config.workerMode,
        requestCustomReferer: this.config.sendCustomRefererHeader ? getGlobalThis().location?.href : undefined,
      },
      this._eventBus,
    );

    this._eventBus.publish(BasePlaylistRotationControllerEvents.playlists.playlistRequested, {});
  }

  protected _handleParsedPlaylist(manifest: IHydratedManifest): void {
    this._lifecycleEventReporter('Playlist parsed');

    const phbMultiplier: number = Math.max(this.config.targetBufferMultiplier, 1);
    this._partHoldBack = getPartHoldback(manifest, manifest.targetDuration || 2) * phbMultiplier;

    if (this._isMultiVariantPlaylist(manifest)) {
      this._handleMultivariantManifestDetected(manifest);

      return;
    }

    if (this._isSimplePlaylist(manifest)) {
      this._handleSimplePlaylistDetected(manifest);

      return;
    }

    if (this._isVODPlaylist(manifest)) {
      this._handleVODPlaylist(manifest);

      return;
    }

    const isLastPlaylistOfSegment: boolean = (manifest.preloadSegment.parts || []).length === 0;

    const partsToLoad: Fragment[] = manifest.segments
      .flatMap((segment) =>
        segment.parts.map(
          ({ uri, independent, partNumber, startTs, endTs, msnNumber, duration }) =>
            new Fragment(
              {
                url: uri,
                independent: independent ? EIndependenceType.INDEPENDENT : EIndependenceType.PART,
                msn: msnNumber,
                pn: partNumber,
                duration,
                isTransmuxingBypassRequested: !!manifest.segments[0].map?.uri,
                startTs,
                endTs,
              },
              this._eventBus,
              this.config.shouldLogFragmentsHistory,
            ),
        ),
      )
      .concat(
        (manifest.preloadSegment.parts || []).map(
          ({ uri, independent = false, msnNumber, partNumber, startTs, endTs, duration }) =>
            new Fragment(
              {
                url: uri,
                independent: independent ? EIndependenceType.INDEPENDENT : EIndependenceType.PART,
                msn: msnNumber,
                pn: partNumber,
                duration,
                isTransmuxingBypassRequested: !!manifest.segments[0].map?.uri,
                startTs,
                endTs,
              },
              this._eventBus,
              this.config.shouldLogFragmentsHistory,
            ),
        ),
      )
      .filter(({ msn, pn }) =>
        this._hasLoadedInitialAmount ? msn > this._nextMSN || (msn === this._nextMSN && pn >= this._nextPN) : true,
      );

    const reversedPartsToLoad: Fragment[] = partsToLoad.reverse();

    const minimalPartIndexToLoadByPartHoldBack: number = reversedPartsToLoad.reduce<{ currentDuration: number; currentIndex: number }>(
      (accumulator, currentPart) => {
        if (accumulator.currentDuration < this._partHoldBack) {
          accumulator.currentIndex += 1;
          accumulator.currentDuration += currentPart.duration;
        }

        return accumulator;
      },
      { currentDuration: 0, currentIndex: -1 },
    ).currentIndex;

    const minimalPartIndexToLoadByLastFragment: number = this._lastLoadedFragment
      ? reversedPartsToLoad.findIndex((frag) => (frag.startTs || 0) < (this._lastLoadedFragment?.endTs || 0))
      : -1;

    const minimalPartIndexToLoad: number = Math.min(
      reversedPartsToLoad.length,
      Math.max(minimalPartIndexToLoadByPartHoldBack, minimalPartIndexToLoadByLastFragment) + this.config.additionalStartParts,
    );

    this._logger.debug(
      `Minimal part ${reversedPartsToLoad[minimalPartIndexToLoad]?.msn}:${reversedPartsToLoad[minimalPartIndexToLoad]?.pn}(${new Date(
        reversedPartsToLoad[minimalPartIndexToLoad]?.startTs || 0,
      )})`,
    );

    let nearestKeyframePartIndex: number = reversedPartsToLoad.findIndex(
      ({ independent }, index) => index >= minimalPartIndexToLoad && independent === EIndependenceType.INDEPENDENT,
    );

    if (nearestKeyframePartIndex === -1) {
      nearestKeyframePartIndex = reversedPartsToLoad.length - 1;
    }

    this._logger.debug(
      `Part to start load from: ${reversedPartsToLoad[nearestKeyframePartIndex]?.msn}:${reversedPartsToLoad[nearestKeyframePartIndex]?.pn}`,
    );

    const filteredParts: Fragment[] =
      nearestKeyframePartIndex > -1 ? reversedPartsToLoad.slice(0, nearestKeyframePartIndex + 1).reverse() : partsToLoad;

    if (
      this.config.blockingMode === EBlockingMode.WAIT_FOR_PREFETCH_SEGMENT &&
      Array.isArray(manifest.preloadSegment.preloadHints) &&
      manifest.preloadSegment.preloadHints.length
    ) {
      this._awaitingFragmentURL = manifest.preloadSegment.preloadHints[0]?.uri || null;

      filteredParts.push(
        ...manifest.preloadSegment.preloadHints.map(
          ({ uri, msnNumber, partNumber }) =>
            new Fragment(
              {
                url: uri,
                independent: EIndependenceType.UNKNOWN,
                msn: msnNumber,
                pn: partNumber,
                duration: 0,
                isTransmuxingBypassRequested: !!manifest.segments[0].map?.uri,
              },
              this._eventBus,
              this.config.shouldLogFragmentsHistory,
            ),
        ),
      );
    }

    if (manifest.segments[0].map?.uri) {
      filteredParts.unshift(
        new Fragment(
          {
            playlistUrl: this._url || '',
            fragmentUrl: manifest.segments[0].map?.uri,
            duration: 0,
            independent: EIndependenceType.INDEPENDENT,
            msn: -1,
            pn: -1,
            isTransmuxingBypassRequested: true,
            isInitSection: true,
          },
          this._eventBus,
          this.config.shouldLogFragmentsHistory,
        ),
      );
    }

    this._handleFragmentsDetected(filteredParts);

    this._hasLoadedInitialAmount = true;

    this._nextMSN = manifest.mediaSequence + manifest.segments.length;
    this._nextPN = (manifest.preloadSegment.parts || []).length;

    this.additionalInfo.msn = this._nextMSN;
    this.additionalInfo.pn = this._nextPN;
    this.status = 'Awaiting fragment to load next playlist';

    if (isLastPlaylistOfSegment) {
      this._eventBus.publish(BasePlaylistRotationControllerEvents.playlists.lastPlaylistOfSegmentLoaded, {});
    }

    if (this.config.blockingMode === EBlockingMode.WAIT_FOR_PLAYLIST) {
      this._loadNextPlaylist();

      return;
    }
  }

  protected _planNextLoad(): void {
    this._playlistLoaderTimeout = getGlobalThis().setTimeout(() => {
      this._loadNextPlaylist();
    }, this.config.retryLowLatencyPlaylistTimeout);
  }

  private _buildUrl(): URL {
    const url: URL = new URL(this._url || '');

    const finalSearchParams: URLSearchParams = new URLSearchParams([
      ...url.searchParams,
      ...this._playlistDecryptHandler.corruptionQueryParams,
    ]);

    if (this._nextPN !== -1 && this._nextMSN !== -1) {
      finalSearchParams.set('_HLS_msn', `${this._nextMSN}`);
      finalSearchParams.set('_HLS_part', `${this._nextPN}`);
    }

    url.search = finalSearchParams.toString();
    return url;
  }

  @bind
  private _onFragmentFinished(frag: Fragment): void {
    this._logger.debug(`${frag.startTs} -> ${frag.endTs} loaded (${frag.getMsnPn()})`);
    if (this.config.blockingMode === EBlockingMode.WAIT_FOR_PREFETCH_SEGMENT && this._awaitingFragmentURL === frag.url) {
      this._logger.debug('Prefetch segment is ready now, loading next playlist');
      this._loadNextPlaylist();
    }
  }

  @bind
  private _onFragmentAdded(fragment: Fragment): void {
    this._lastLoadedFragment = fragment;
  }
}
